import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Button from "src/components/app/Button";
import Layout from "src/components/app/layout/Layout";
import Radio from "src/components/common/Radio";
import { POST_MESSAGE_EVENTS } from "src/constants/common";
import { sendMessageToParent } from "src/helpers/common";
import Header from "src/components/app/layout/Header";
import WhatsAppIcon from "src/assets/images/common/whatsapp.png";

type HelpOption =
  | "Purchasing New CGM"
  | "Connecting My CGM"
  | "Having troubles after connection";

const WHATSAPP_BOT_URL = "https://wa.aisensy.com/aaa1qv";

const CGMChatWithExperts = () => {
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"] ?? "";
  const [selectedOption, setSelectedOption] = useState<HelpOption | null>(null);

  const helpOptions = useMemo<HelpOption[]>(
    () => [
      "Purchasing New CGM",
      "Connecting My CGM",
      "Having troubles after connection",
    ],
    []
  );

  const openWhatsAppSupport = () => {
    if (!selectedOption) return;

    if (window.ReactNativeWebView) {
      sendMessageToParent(POST_MESSAGE_EVENTS.OPEN_EXTERNAL_LINK, {
        link: WHATSAPP_BOT_URL,
      });
      return;
    }

    window.open(WHATSAPP_BOT_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <Layout
      supportProgramKey={supportProgramKey}
      enableTheme={true}
      containerClassName="bg-white"
      className="bg-white"
      header={
        <Header
          title="Chat with experts"
          showBackButton={true}
          onBackClick={() => navigate(-1)}
        />
      }
      footer={
        <div className="border-t border-gray-100 bg-white px-4 py-4">
          <Button
            fullWidth
            rounded="full"
            disabled={!selectedOption}
            onClick={openWhatsAppSupport}
          >
            <span className="flex items-center justify-center gap-2">
              <img
                src={WhatsAppIcon}
                alt=""
                aria-hidden="true"
                className="h-4 w-4 object-contain"
              />
              Proceed
            </span>
          </Button>
        </div>
      }
    >
      <div className="px-4 pt-6 pb-6">
        <h1 className="text-[28px] font-semibold text-[#101828] font-playfair">
          Chat with our experts
        </h1>
        <p className="mt-3 text-sm leading-6 text-[#667085]">
          We are here to help. Let us know where you are facing any difficulty
          and chat with our expert team who will guide you in this journey
        </p>

        <div className="mt-8">
          <p className="text-base font-semibold text-[#101828]">
            What do you want help with?
          </p>

          <ul className="mt-4 flex flex-col gap-3">
            {helpOptions.map((option) => (
              <li key={option}>
                <button
                  type="button"
                  onClick={() => setSelectedOption(option)}
                  className={`flex w-full items-center gap-3 rounded-xl border px-4 py-4 text-left ${
                    selectedOption === option
                      ? "border-[var(--app-primary)] bg-[var(--app-primary-bg)]"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <Radio
                    checked={selectedOption === option}
                    onChange={() => setSelectedOption(option)}
                  />
                  <span className="text-sm font-medium text-[#101828]">
                    {option}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default CGMChatWithExperts;
